import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { MOCK_INSIGHTS } from '../data/mockData';

const Insights = () => {
  // ECharts configuration for a futuristic line chart
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(19, 19, 29, 0.9)',
      borderColor: 'rgba(110, 231, 183, 0.2)',
      textStyle: { color: '#f3f4f6' }
    },
    grid: {
      top: '10%', left: '3%', right: '4%', bottom: '3%', containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { lineStyle: { color: '#2d2d42' } },
      axisLabel: { color: '#9ca3af' }
    },
    yAxis: {
      type: 'value',
      show: false, // Hide y-axis for cleaner look
    },
    series: [
      {
        name: 'Mood Index',
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          width: 3,
          color: {
            type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [{ offset: 0, color: '#6ee7b7' }, { offset: 1, color: '#d8b4fe' }]
          }
        },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(110, 231, 183, 0.3)' },
              { offset: 1, color: 'rgba(216, 180, 254, 0)' }
            ]
          }
        },
        data: [3, 4, 2, 5, 4, 6, 5]
      }
    ]
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Weekly Synthesis</h1>
          <p className="text-gray-400 text-sm">AI-driven pattern recognition across your emotional data.</p>
        </header>

        {/* Chart Section */}
        <div className="glass-panel rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-200">Emotional Resonance</h2>
            <select className="bg-ink-800 border border-ink-600 text-xs text-gray-300 rounded-md px-2 py-1 outline-none">
              <option>This Week</option>
              <option>Last Week</option>
              <option>This Month</option>
            </select>
          </div>
          <div className="h-64 w-full">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        {/* Insight Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {MOCK_INSIGHTS.map((insight, idx) => (
            <motion.div 
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 + 0.2 }}
              className={`glass-panel rounded-2xl p-6 border-t-2 ${
                insight.type === 'positive' ? 'border-t-aurora' : 'border-t-lilac'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${
                  insight.type === 'positive' ? 'bg-aurora/10 text-aurora' : 'bg-lilac/10 text-lilac'
                }`}>
                  <SafeIcon name={insight.type === 'positive' ? 'TrendingUp' : 'AlertCircle'} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-100 mb-2">{insight.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tag Cloud / Distribution */}
        <div className="mt-8 glass-panel rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-gray-200 mb-4">Top Focus Areas</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { tag: '#deepWork', count: 12, color: 'text-aurora bg-aurora/10 border-aurora/20' },
              { tag: '#fundraising', count: 8, color: 'text-lilac bg-lilac/10 border-lilac/20' },
              { tag: '#hiring', count: 5, color: 'text-gray-300 bg-ink-700 border-ink-600' },
              { tag: '#burnout', count: 3, color: 'text-red-400 bg-red-400/10 border-red-400/20' },
            ].map(item => (
              <div key={item.tag} className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${item.color}`}>
                <span className="text-sm font-medium">{item.tag}</span>
                <span className="text-xs opacity-70">({item.count})</span>
              </div>
            ))}
          </div>
        </div>

      </motion.div>
    </div>
  );
};

export default Insights;